/**
 * Invoice domain vocabulary — the enums and the state machine shared by the
 * API, the typed clients and the web app.
 *
 * Distinct from `invoice.ts`, which is the *document* contract (what gets
 * rendered to a PDF). This file describes the *record*: its lifecycle, how it
 * is classified, and what a line item is. A document has no status; a record
 * does.
 */

/**
 * What kind of engagement the invoice bills. Deliberately the same vocabulary
 * as the derived `sowType` badge in `docs/sows.md`, because an invoice's type
 * is a consequence of the SOW lines it was raised from — `retainer` when every
 * line is a retainer line, `tm` when every line is time & materials, `hybrid`
 * when both appear.
 *
 * Unlike `sowType` this IS stored: an invoice is a document that was issued,
 * and it has to keep saying what it said even after the SOW behind it is
 * amended into a different shape.
 */
export const INVOICE_TYPES = ['retainer', 'tm', 'hybrid'] as const;
export type InvoiceType = (typeof INVOICE_TYPES)[number];

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  retainer: 'Retainer',
  tm: 'Time & materials',
  hybrid: 'Hybrid',
};

/**
 * Lifecycle. Three states, in one direction, with exactly one reversal.
 *
 *   draft ──approve──► approved ──send──► sent
 *     ▲                    │
 *     └──── unapprove ─────┘
 *
 * `sent` is terminal. Once a document has left for a customer's inbox, the
 * record of what they received must not change — an invoice that could be
 * edited after sending is one where the copy on file and the copy in the
 * customer's hands can silently disagree. Corrections are a new invoice (or a
 * credit line on the next one), which is also how the accounting works.
 *
 * `approved → draft` exists because approving is a bulk action over a filtered
 * list, and a bulk action with no undo is a trap. It is only reachable before
 * anything has been sent.
 */
export const INVOICE_STATUSES = ['draft', 'approved', 'sent'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  sent: 'Sent',
};

/**
 * What became of the email a sent invoice went out in — the LAST MEANINGFUL
 * thing SES reported, not the latest event. Listed WEAKEST FIRST: the order
 * of this array IS the precedence (see {@link invoiceEmailStatusRank}), so
 * `complained` › `opened` › `delivered` › `bounced` › `sent`. An open after a
 * delivery is news; a delivery arriving after an open (SNS does not keep
 * order) is not, so it never pulls the status back down.
 *
 * `bounced` ranks below `delivered` because an invoice goes to several
 * addresses at once: when one bounces and another accepts it, the customer
 * still has the invoice. The bounce is kept (`emailBouncedAt`) and shown in
 * the details, just not as the headline.
 *
 * Null on an invoice that was never emailed — drafts, and invoices marked sent
 * by hand.
 */
export const INVOICE_EMAIL_STATUSES = [
  'sent',
  'bounced',
  'delivered',
  'opened',
  'complained',
] as const;
export type InvoiceEmailStatus = (typeof INVOICE_EMAIL_STATUSES)[number];

/** A status's precedence: 1 for the weakest, rising with strength. */
export function invoiceEmailStatusRank(status: InvoiceEmailStatus): number {
  return INVOICE_EMAIL_STATUSES.indexOf(status) + 1;
}

/** `sent` says "Email sent", not "Sent": it sits beside the invoice's own
 *  Sent status chip, and two chips both reading "Sent" says nothing.
 *  `complained` is a bare "Spam" to fit the list's Email column; the hover
 *  details spell out "Marked as spam" with its date. */
export const INVOICE_EMAIL_STATUS_LABELS: Record<InvoiceEmailStatus, string> = {
  sent: 'Email sent',
  delivered: 'Delivered',
  opened: 'Opened',
  bounced: 'Bounced',
  complained: 'Spam',
};

/** Legal next states for each status. The empty array for `sent` is the
 *  terminal-state assertion, not an oversight. */
export const INVOICE_STATUS_TRANSITIONS: Record<InvoiceStatus, readonly InvoiceStatus[]> = {
  draft: ['approved'],
  approved: ['sent', 'draft'],
  sent: [],
};

export function canTransitionInvoice(from: InvoiceStatus, to: InvoiceStatus): boolean {
  return INVOICE_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Whether anything on the invoice may still change. Nothing once it is sent;
 * on an approved invoice, only WHEN it is sent — its content is locked, see
 * {@link isInvoiceContentEditable}.
 */
export function isInvoiceEditable(status: InvoiceStatus): boolean {
  return status !== 'sent';
}

/**
 * Whether the invoice's content (dates, bill-to, recipients, line items,
 * notes, attachments) may be edited. Only a draft's: approval locks the
 * document the approver signed off on, and changing it means returning it to
 * draft first — in the open, with the approval visibly dropped.
 */
export function isInvoiceContentEditable(status: InvoiceStatus): boolean {
  return status === 'draft';
}

/**
 * What a line represents. Not cosmetic: the retainer run derives `service` and
 * `expense` lines from different sources, and `discount` lines are the reason
 * the invoice has no header-level discount field.
 *
 * A percentage discount is resolved to a dollar amount at the moment it is
 * added and stored as a negative `discount` line. Storing the percentage on
 * the header instead would mean the printed figure silently changes whenever a
 * line above it is edited — an invoice has to keep saying what it said.
 */
export const INVOICE_LINE_KINDS = ['service', 'expense', 'discount', 'adjustment'] as const;
export type InvoiceLineKind = (typeof INVOICE_LINE_KINDS)[number];

export const INVOICE_LINE_KIND_LABELS: Record<InvoiceLineKind, string> = {
  service: 'Service',
  expense: 'Expense',
  discount: 'Discount',
  adjustment: 'Adjustment',
};

/**
 * Where a LINE came from — `invoice_line_items.source_type`, paired with
 * `source_id`.
 *
 * Here rather than in the API because three call sites now need them: the
 * month-end run writes them, the invoice service reads `sow_retainer_line` to
 * classify an invoice's type, and the editor writes `customer_expense` when an
 * operator bills a standing charge by hand. They were duplicated across two
 * API files before the third arrived.
 *
 * A line with no `source_type` was typed by a human and traces to nothing.
 */
export const SOW_RETAINER_SOURCE = 'sow_retainer_line';
export const CUSTOMER_EXPENSE_SOURCE = 'customer_expense';
/** A rolled-up month of timesheet hours for one service role, priced from the
 *  SOW's T&M line. One line per role, never per person — see
 *  `docs/invoice-generation.md`. */
export const SOW_TM_SOURCE = 'sow_tm_line';

/** Where the invoice came from. `retainer_run` marks the rows the month-end
 *  job created, which is what lets a re-run tell "already generated" from
 *  "an operator raised this by hand for the same period". */
export const INVOICE_SOURCES = ['manual', 'retainer_run', 'tm_run'] as const;
export type InvoiceSource = (typeof INVOICE_SOURCES)[number];

export const INVOICE_SOURCE_LABELS: Record<InvoiceSource, string> = {
  manual: 'Created manually',
  retainer_run: 'Month-end retainer run',
  tm_run: 'Time & materials run',
};

/**
 * Which engine a generation run drives.
 *
 * The two are separate runs, not two filters on one, because they answer to
 * different clocks. A retainer run fires at month end and prices an agreement;
 * a T&M run is pressed by hand once the month's timesheets have settled, and
 * prices hours that did not exist when the retainer run went out.
 *
 * Stored on the run so its history says which engine produced it, and carried
 * on `invoices.source` as `retainer_run` / `tm_run` so a re-run of either can
 * tell its own output from the other's.
 */
export const INVOICE_GENERATION_KINDS = ['retainer', 'tm'] as const;
export type InvoiceGenerationKind = (typeof INVOICE_GENERATION_KINDS)[number];

export const INVOICE_GENERATION_KIND_LABELS: Record<InvoiceGenerationKind, string> = {
  retainer: 'Retainers',
  tm: 'Time & materials',
};

/** The `invoices.source` each run kind stamps. One place, so the uniqueness
 *  indexes and the run that depends on them cannot drift apart. */
export const INVOICE_SOURCE_BY_GENERATION_KIND: Record<InvoiceGenerationKind, InvoiceSource> = {
  retainer: 'retainer_run',
  tm: 'tm_run',
};

export function isInvoiceGenerationKind(v: unknown): v is InvoiceGenerationKind {
  return typeof v === 'string' && (INVOICE_GENERATION_KINDS as readonly string[]).includes(v);
}

/** Type guards for reading CHECK-constrained text columns back off a row.
 *  Validate rather than blind-cast so a value that got past the constraint
 *  surfaces as a fallback rather than an unsound type. */
export function isInvoiceStatus(v: unknown): v is InvoiceStatus {
  return typeof v === 'string' && (INVOICE_STATUSES as readonly string[]).includes(v);
}
export function isInvoiceType(v: unknown): v is InvoiceType {
  return typeof v === 'string' && (INVOICE_TYPES as readonly string[]).includes(v);
}
export function isInvoiceLineKind(v: unknown): v is InvoiceLineKind {
  return typeof v === 'string' && (INVOICE_LINE_KINDS as readonly string[]).includes(v);
}

/**
 * Derive the invoice type from the kinds of work billed.
 *
 * Mirrors the `sowType` derivation in `docs/sows.md` so an invoice raised from
 * a hybrid SOW is labelled the same way the SOW is. Returns null when there is
 * nothing to classify — an empty draft — and the caller decides whether that
 * is an error (it is, on approve) or simply "not yet" (it is, on create).
 */
export function deriveInvoiceType(
  lines: ReadonlyArray<{ kind: InvoiceLineKind; retainer: boolean }>,
): InvoiceType | null {
  // Discounts and adjustments describe the money, not the work — an invoice
  // that is one retainer line plus a discount is still a retainer invoice.
  const billable = lines.filter((l) => l.kind === 'service' || l.kind === 'expense');
  if (billable.length === 0) return null;
  const hasRetainer = billable.some((l) => l.retainer);
  const hasTm = billable.some((l) => !l.retainer);
  if (hasRetainer && hasTm) return 'hybrid';
  return hasRetainer ? 'retainer' : 'tm';
}

// ---- Invoicing pause ------------------------------------------------------

/**
 * An invoicing pause, as a half-open date window `[from, until)`.
 *
 * Carried by `customers` and by `sows`, with identical column names and
 * identical meaning, so this one predicate answers "is invoicing paused?" for
 * either — and neither can drift into its own dialect of the same idea.
 *
 * A window, not a flag. The boolean it replaced could only say "paused, as of
 * whenever someone last touched the row", which made two things impossible:
 * scheduling a pause that starts next month, and reading history correctly. A
 * flag is evaluated against the wall clock, so re-running a missed month-end
 * close asked "is this customer paused NOW?" instead of "was it paused for the
 * period being billed?" — and re-billing an already-closed month could produce
 * a different answer than the original run did. The window is a fact about
 * dates, so both questions resolve the same way forever.
 *
 * `from` is what decides whether a pause exists at all; `until` alone is
 * meaningless and the CHECK constraints on both tables forbid it.
 */
export interface InvoicingPauseWindow {
  /** First day the pause applies. `null` = not paused. */
  invoicingPauseFrom: string | null;
  /** First day invoicing RESUMES — exclusive, matching every other range in
   *  this codebase. `null` with a `from` set = paused indefinitely. */
  invoicingPauseUntil: string | null;
}

/**
 * True when invoicing is paused on `onDate` (a `YYYY-MM-DD` string).
 *
 * Judged against a caller-supplied date, never the clock, so the month-end run
 * can ask about the period it is billing rather than about today. ISO dates
 * compare correctly as strings, which is why there is no Date parsing here.
 */
export function isInvoicingPausedOn(window: InvoicingPauseWindow, onDate: string): boolean {
  const { invoicingPauseFrom: from, invoicingPauseUntil: until } = window;
  // No start date means no pause — including the case where `until` somehow
  // survived without one, which the CHECK constraints make unreachable.
  if (from === null) return false;
  if (onDate < from) return false;
  // An expired pause is not a pause. Without this a customer paused "until
  // March" would still be skipped in April, and nobody would notice until they
  // asked why they hadn't been invoiced.
  return until === null || onDate < until;
}

// ---- Attachments ----------------------------------------------------------

/**
 * The file types an invoice accepts as attachments, by extension, with the
 * content type each is stored and served as.
 *
 * Keyed on the EXTENSION rather than on the type the browser reports. Browsers
 * disagree about CSV and XLSX (some send `application/octet-stream`), and a
 * client-supplied type is not something to store and later serve back
 * verbatim — the server decides what these bytes are called.
 *
 * Shared by the API (which enforces it) and the web app (which screens files
 * before uploading them), so the two cannot disagree about what is allowed.
 */
export const INVOICE_ATTACHMENT_TYPES: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

/** The `accept` attribute for a file input that offers exactly these types. */
export const INVOICE_ATTACHMENT_ACCEPT = Object.keys(INVOICE_ATTACHMENT_TYPES)
  .map((ext) => `.${ext}`)
  .join(',');

/** Largest single attachment: 10 MB. */
export const MAX_INVOICE_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Most attachments one invoice may carry. */
export const MAX_INVOICE_ATTACHMENTS = 5;

const ATTACHMENT_TYPES_BY_EXTENSION = new Map(Object.entries(INVOICE_ATTACHMENT_TYPES));

/**
 * The content type an attachment is stored as, or `null` when its extension
 * is not allowed. A Map lookup rather than `in` on the record, so a filename
 * like `x.constructor` cannot match an inherited property.
 */
export function invoiceAttachmentContentType(filename: string): string | null {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return null;
  return ATTACHMENT_TYPES_BY_EXTENSION.get(filename.slice(dot + 1).toLowerCase()) ?? null;
}

// ---- Sending ---------------------------------------------------------------

/**
 * Largest invoice email: the PDF plus attachments, in raw bytes, 7 MB.
 *
 * SES caps a raw message at 10 MB AFTER base64 encoding, which inflates
 * attachments by a third — about 7.5 MB of files. 7 MB leaves room for the
 * body, headers and MIME boundaries. Over it, an invoice is not sent at all:
 * a customer missing an attachment they were promised is worse than a delay.
 */
export const MAX_INVOICE_EMAIL_BYTES = 7 * 1024 * 1024;

/** Additional To addresses one on-demand send may carry — the Send dialog and
 *  the API enforce the same number. */
export const MAX_ADDITIONAL_SEND_RECIPIENTS = 20;

// ---- Recipients ------------------------------------------------------------

/** Where an address sits on the invoice email. */
export const INVOICE_RECIPIENT_KINDS = ['to', 'cc'] as const;
export type InvoiceRecipientKind = (typeof INVOICE_RECIPIENT_KINDS)[number];

/**
 * One address the invoice is emailed to. An invoice holds one ordered list of
 * these — To and CC alike — so an address is on the email exactly once, and
 * moving it between To and CC is a change of `kind`, not a move between two
 * fields. Delivery only: nothing here is printed on the document.
 */
export interface InvoiceRecipient {
  email: string;
  kind: InvoiceRecipientKind;
}

/** Most recipients one invoice can carry, To and CC together. */
export const MAX_INVOICE_RECIPIENTS = 40;

/**
 * A recipient list as it is stored: addresses trimmed, blanks dropped, and
 * each address once, compared case-insensitively. The FIRST occurrence wins,
 * so a list written To-first keeps an address in To when the same one was
 * also typed as a CC. Order is otherwise kept — it is the order on the email.
 */
export function normalizeInvoiceRecipients(
  list: readonly InvoiceRecipient[] | null | undefined,
): InvoiceRecipient[] {
  const seen = new Set<string>();
  const out: InvoiceRecipient[] = [];
  for (const r of list ?? []) {
    const email = r.email.trim();
    const key = email.toLowerCase();
    if (email === '' || seen.has(key)) continue;
    seen.add(key);
    out.push({ email, kind: r.kind });
  }
  return out;
}

/** A billing contact's addresses as recipients: its email as the To, its CC
 *  list as CCs. What picking a contact puts on an invoice. */
export function recipientsFromContact(
  contact: { email?: string | null; ccEmails?: readonly string[] | null } | null | undefined,
): InvoiceRecipient[] {
  if (!contact) return [];
  return normalizeInvoiceRecipients([
    ...(contact.email ? [{ email: contact.email, kind: 'to' as const }] : []),
    ...(contact.ccEmails ?? []).map((email) => ({ email, kind: 'cc' as const })),
  ]);
}

/** The addresses of one kind, in order — what goes on the email's To or CC line. */
export function recipientAddresses(
  list: readonly InvoiceRecipient[] | null | undefined,
  kind: InvoiceRecipientKind,
): string[] {
  return (list ?? []).filter((r) => r.kind === kind).map((r) => r.email);
}

/** When scheduled invoices go out, until a tenant sets its own: 9:00 UTC. */
export const DEFAULT_INVOICE_SEND_HOUR = 9;
export const DEFAULT_INVOICE_SEND_TIMEZONE = 'UTC';
